// React-context som bär designläge-interaktion (handlers + state) ner till
// nestade renderare. Med detta slipper vi prop-drilla samma callbacks genom
// BlockView → TwoColumnsBlockView → BlockList → BlockContainer.

import { createContext, ReactNode, useContext } from 'react';
import { DropTarget } from './blockOps';
import { BlockId, BlockType, EmailDocSettings } from './types';

export interface BlockCanvasContextValue {
  selectedBlockId: BlockId | null;
  // Doc-settings (bg-färger, typsnitt) — block-views läser dessa så att
  // canvas-rendering speglar valen i sidopanelen live.
  docSettings: EmailDocSettings;
  onSelectBlock: (id: BlockId | null) => void;
  onInsertBlock: (type: BlockType, target: DropTarget) => void;
  onReorderBlock: (id: BlockId, target: DropTarget) => void;
  onMoveBlock: (id: BlockId, direction: 'up' | 'down') => void;
  onDuplicateBlock: (id: BlockId) => void;
  onDeleteBlock: (id: BlockId) => void;
  // Räknar ut om blocket är först/sist i sin förälder — toolbar:en behöver
  // detta för att grå-out "flytta upp"/"flytta ner" på rätt sätt även för
  // nestade block.
  getMoveCapabilities: (id: BlockId) => { canMoveUp: boolean; canMoveDown: boolean };
  // Global single-source-of-truth för vilken BlockList som visar drop-indikator
  // just nu. Förhindrar dubbla indikatorer när cursor är i en nestad kolumn.
  activeDragTarget: { listId: string; index: number } | null;
  setActiveDragTarget: (target: { listId: string; index: number } | null) => void;
}

const BlockCanvasContext = createContext<BlockCanvasContextValue | null>(null);

export const BlockCanvasProvider = ({ value, children }: { value: BlockCanvasContextValue; children: ReactNode }) => (
  <BlockCanvasContext.Provider value={value}>{children}</BlockCanvasContext.Provider>
);

// Returnerar canvas-context. Returnerar null om komponenten renderas utanför
// en provider — då ska komponenten falla tillbaka till passiv (non-interactive)
// rendering, t.ex. om BlockView används i en preview-pane.
export const useBlockCanvas = (): BlockCanvasContextValue | null => useContext(BlockCanvasContext);
